import type { RealtimeMessage } from '@simulador/realtime-protocol';
import type { Choice, EventDetail, Quorum, Seat, VotingView } from './types.ts';

/** Estado de una pantalla de sesión: se arma por HTTP y se mantiene con los avisos en vivo. */
export type ScreenState = {
  eventStatus: EventDetail['event']['status'];
  eventVersion: number;
  seats: Seat[];
  quorum: Quorum;
  voting: VotingView | null;
  /** Voto de cada persona; solo en votaciones nominales. */
  votes: Record<string, Choice>;
};

export function fromSnapshot(detail: EventDetail, voting: VotingView | null): ScreenState {
  return {
    eventStatus: detail.event.status,
    eventVersion: detail.event.version,
    seats: detail.attendance,
    quorum: detail.quorum,
    voting,
    votes: Object.fromEntries((voting?.votes ?? []).map((v) => [v.memberId, v.choice])),
  };
}

/** Aplica un aviso. Es pura: devuelve un estado nuevo o el mismo si el aviso no aplica. */
export function apply(state: ScreenState, message: RealtimeMessage): ScreenState {
  switch (message.type) {
    case 'event.status':
      if (message.version <= state.eventVersion) return state;
      return { ...state, eventStatus: message.status as ScreenState['eventStatus'], eventVersion: message.version };
    case 'attendance':
      return {
        ...state,
        quorum: message.quorum,
        seats: state.seats.map((s) => (s.memberId === message.memberId ? { ...s, present: message.present } : s)),
      };
    case 'voting': {
      const incoming = message.voting;
      const same = state.voting?.id === incoming.id;
      // Avisos atrasados de la misma votación no la regresan a un estado anterior.
      if (same && state.voting!.version > incoming.version) return state;
      return {
        ...state,
        voting: { ...(same ? state.voting! : {}), ...incoming, result: incoming.result as VotingView['result'] },
        votes: same ? state.votes : {},
      };
    }
    case 'ballot': {
      if (!state.voting || state.voting.id !== message.votingId) return state;
      // ballotsCast solo crece: un aviso atrasado no lo reduce.
      if (message.ballotsCast < state.voting.ballotsCast) return state;
      return {
        ...state,
        voting: { ...state.voting, ballotsCast: message.ballotsCast, tally: message.tally },
        votes: message.vote ? { ...state.votes, [message.vote.memberId]: message.vote.choice } : state.votes,
      };
    }
    case 'minutes':
      return state;
  }
}
